import { useState, useEffect, useCallback, useMemo } from "react";
import { db } from "@/services/firebaseService";
import { getOffersByLocationId } from "@/services/offerService";
import { getAllSortableOptions } from "@/services/getSortableOptions";
import { useLocationsByOrganization } from "./useLocationsByOrganization";
import { Offer } from "@/types/offer";
import { CampLocation } from "@/types/campLocation";
import { SortableOption } from "@/types/fieldsOptions";
import { useFirestoreError } from "./useFirestoreError";
import {
  enhanceOffersWithOptions,
  filterOffersForTableView,
  type TableDateFilter,
} from "@/services/offer/offerDisplayUtils";

export interface UseOffersByOrganizationResult {
  offers: Offer[];
  filteredTableOffers: Offer[];
  isLoading: boolean;
  locations: CampLocation[];
  tableDateFilter: TableDateFilter;
  setTableDateFilter: (v: TableDateFilter) => void;
  availabilityStatusOptions: SortableOption[];
  signupStatusOptions: SortableOption[];
  refreshOffers: () => Promise<void>;
  allSortableOptionsMap: Record<string, SortableOption>;
}

export function useOffersByOrganization(
  organizationId: string | null
): UseOffersByOrganizationResult {
  const { handleFirestoreError } = useFirestoreError();
  const {
    locations,
    isLoading: locationsLoading,
    refreshLocations,
  } = useLocationsByOrganization(organizationId, {
    skipLocationAccessGroupFilter: true,
  });

  const [offers, setOffers] = useState<Offer[]>([]);
  const [offersLoading, setOffersLoading] = useState(true);
  const [tableDateFilter, setTableDateFilter] = useState<TableDateFilter>("future");
  const [allSortableOptionsMap, setAllSortableOptionsMap] = useState<
    Record<string, SortableOption>
  >({});
  const [availabilityStatusOptions, setAvailabilityStatusOptions] = useState<
    SortableOption[]
  >([]);
  const [signupStatusOptions, setSignupStatusOptions] = useState<SortableOption[]>(
    []
  );

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const optionsMap = await getAllSortableOptions(db);
        setAllSortableOptionsMap(optionsMap);
        setAvailabilityStatusOptions(
          Object.values(optionsMap)
            .filter((o) => o.type === "availStatus")
            .sort((a, b) => a.sortOrder - b.sortOrder)
        );
        setSignupStatusOptions(
          Object.values(optionsMap)
            .filter((o) => o.type === "signUpStatus")
            .sort((a, b) => a.sortOrder - b.sortOrder)
        );
      } catch (e) {
        console.error("useOffersByOrganization: Error loading sortable options", e);
      }
    };
    loadOptions();
  }, []);

  const loadOffers = useCallback(async () => {
    if (!organizationId || locations.length === 0) {
      setOffers([]);
      setOffersLoading(false);
      return;
    }
    setOffersLoading(true);
    try {
      const results = await Promise.all(
        locations.map((loc) => getOffersByLocationId(db, loc.id))
      );
      const merged = results.flat();
      const enhanced = enhanceOffersWithOptions(merged, allSortableOptionsMap);
      setOffers(enhanced);
    } catch (e) {
      console.error("useOffersByOrganization: Error loading offers", e);
      handleFirestoreError(e);
      setOffers([]);
    } finally {
      setOffersLoading(false);
    }
  }, [organizationId, locations, allSortableOptionsMap, handleFirestoreError]);

  useEffect(() => {
    if (!organizationId) {
      if (offers.length > 0 || offersLoading) {
        setOffers([]);
        setOffersLoading(false);
      }
      return;
    }
    if (locations.length === 0 && !locationsLoading) {
      if (offers.length > 0 || offersLoading) {
        setOffers([]);
        setOffersLoading(false);
      }
      return;
    }
    if (locations.length > 0 && Object.keys(allSortableOptionsMap).length > 0) {
      loadOffers();
    }
    // Intentionally omit loadOffers — it changes every render (handleFirestoreError not memoized),
    // which would cause this effect to re-run infinitely and flash Loading/rows.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, locations, locationsLoading, allSortableOptionsMap]);

  const filteredTableOffers = useMemo(
    () => filterOffersForTableView(offers, tableDateFilter),
    [offers, tableDateFilter]
  );

  const refreshOffers = useCallback(async () => {
    await refreshLocations();
    await loadOffers();
  }, [refreshLocations, loadOffers]);

  return {
    offers,
    filteredTableOffers,
    isLoading: locationsLoading || offersLoading,
    locations,
    tableDateFilter,
    setTableDateFilter,
    availabilityStatusOptions,
    signupStatusOptions,
    refreshOffers,
    allSortableOptionsMap,
  };
}
